import React, {
	useState,
	useEffect,
	useRef,
	useMemo,
	useCallback,
} from "react";
import { createRoot } from "react-dom/client";
import { Range, getTrackBackground } from "react-range";
import qs from "qs";
import debounce from "lodash.debounce";
import useMediaQuery from "../../useMediaQuery";
import convertUsStateAbbrAndName from "../../us-states";
import loadingGIF from "../../assets/img/loading.gif";
import { FilterIcon } from "../../icons";
import { useSharedFilterToggle } from "../../filtersSync";
import "./view.scss";

// **Constants for ranges**
const PRICEMIN = 1000;
const PRICEMAX = 100000;
const YEARRANGEMIN = 1900;
const YEARRANGEMAX = new Date().getFullYear();
const MILEAGEMIN = 0;
const MILEAGEMAX = 2000000;

/**
 * **Utility function to group and sort categories by parent**
 */
const groupAndSortCategories = (categories) => {
	const grouped = {};
	const topLevelCategories = [];

	categories.forEach((category) => {
		const parentId = category.parent > 0 ? category.parent : null;

		if (!parentId) {
			topLevelCategories.push(category);
		} else {
			if (!grouped[parentId]) {
				grouped[parentId] = [];
			}
			grouped[parentId].push(category);
		}
	});

	// Sort top-level categories alphabetically by name
	topLevelCategories.sort((a, b) => a.name.localeCompare(b.name));

	// Sort child categories alphabetically by name within each group
	Object.keys(grouped).forEach((parentId) => {
		grouped[parentId].sort((a, b) => a.name.localeCompare(b.name));
	});

	return { topLevelCategories, groupedCategories: grouped };
};

const SidebarFiltersComponent = ({
	categories = [],
	makes = [],
	models = [],
	locations = [],
}) => {
	const isMobile = useMediaQuery("(max-width:768px)");
	const { hidden: filtersHidden, toggle: toggleFilters } =
		useSharedFilterToggle(isMobile);
	const formRef = useRef(null);
	const initialMakeIDRef = useRef(null);
	const initialModelsRef = useRef([]);
	const [initialParamsLoaded, setInitialParamsLoaded] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isFetching, setIsFetching] = useState(false);

	// **Initialize state variables**
	const [yearValues, setYearValues] = useState([YEARRANGEMIN, YEARRANGEMAX]);
	const [priceValues, setPriceValues] = useState([PRICEMIN, PRICEMAX]);
	const [mileageValues, setMileageValues] = useState([MILEAGEMIN, MILEAGEMAX]);
	const [sortField, setSortField] = useState("");

	const [selectedCategory, setSelectedCategory] = useState("");
	const [selectedMake, setSelectedMake] = useState("");
	const [selectedModels, setSelectedModels] = useState([]);
	const [selectedLocation, setSelectedLocation] = useState({});
	const [availableMakes, setAvailableMakes] = useState(makes);
	const [availableModels, setAvailableModels] = useState(models);
	const [availableLocations, setAvailableLocations] = useState(locations);
	const [isLocationsCollapsed, setIsLocationsCollapsed] = useState(isMobile);
	const [collapsedStates, setCollapsedStates] = useState({});
	const [partialSelectionStates, setPartialSelectionStates] = useState({});
	const [priceChangedByUser, setPriceChangedByUser] = useState(false);
	const [yearChangedByUser, setYearChangedByUser] = useState(false);
	const [mileageChangedByUser, setMileageChangedByUser] = useState(false);

	const [filtersChanged, setFiltersChanged] = useState(false);

	// **Refs to hold the latest state values for use in callbacks**
	const availableMakesRef = useRef(availableMakes);
	const availableModelsRef = useRef(availableModels);
	const availableLocationsRef = useRef(availableLocations);
	const yearValuesRef = useRef(yearValues);
	const priceValuesRef = useRef(priceValues);

	const [categoryInfo, setCategoryInfo] = useState(false);
	useEffect(() => {
		// grab the outer wrapper and the inner filters stack
		const productDetailWrapper = document.querySelector(
			".wp-block-rts-woo-blocks-product-detail",
		);
		const wrapper = document.querySelector(".search-filters-stack");
		const filtersStack = document.querySelector(".sidebar-filters");
		if (!wrapper || !filtersStack) return;
		
		const ro = new ResizeObserver(() => {
			wrapper.style.minHeight = `${filtersStack.getBoundingClientRect().height}px`;
			productDetailWrapper.style.minHeight = `${filtersStack.getBoundingClientRect().height}px`;
		});

		ro.observe(filtersStack);
		// do it once immediately
		wrapper.style.minHeight = `${filtersStack.getBoundingClientRect().height}px`;

		return () => {
			ro.disconnect();
		};
	}, []);

	useEffect(() => {
		const sidebar = document.querySelector(".search-filters-stack");
		if (!sidebar) return;
		sidebar.classList.toggle("is-hidden", filtersHidden);
	}, [filtersHidden]);

	// **Update refs whenever their respective states change**
	useEffect(() => {
		availableMakesRef.current = availableMakes;
	}, [availableMakes]);

	useEffect(() => {
		availableModelsRef.current = availableModels;
	}, [availableModels]);

	useEffect(() => {
		availableLocationsRef.current = availableLocations;
	}, [availableLocations]);

	useEffect(() => {
		yearValuesRef.current = yearValues;
	}, [yearValues]);

	useEffect(() => {
		priceValuesRef.current = priceValues;
	}, [priceValues]);

	// **Memoize the makes and models to display**
	const makesToDisplay = useMemo(
		() => (availableMakes.length ? availableMakes : makes),
		[availableMakes, makes],
	);
	const modelsToDisplay = useMemo(
		() => (availableModels.length ? availableModels : models),
		[availableModels, models],
	);

	// **Group and sort categories using useMemo**
	const { topLevelCategories, groupedCategories } = useMemo(
		() => groupAndSortCategories(categories),
		[categories],
	);

	// **Normalize locations and sort them**
	const normalizeLocations = (locationsData) => {
		const normalized = {};
		Object.entries(locationsData).forEach(([key, location]) => {
			const { name, slug } = location;
			let [city, state] = name.split(",").map((part) => part.trim());
			if (!city || !state) return;

			if (!normalized[state]) normalized[state] = [];
			normalized[state].push({
				key,
				value: `${city}, ${state}`,
				slug,
			});
		});

		// Sort the locations within each state alphabetically
		for (const state in normalized) {
			normalized[state].sort((a, b) => a.value.localeCompare(b.value));
		}

		return normalized;
	};

	// **Define normalizedLocations and sortedLocations**
	const normalizedLocations = useMemo(
		() => normalizeLocations(availableLocations),
		[availableLocations],
	);

	const sortedLocations = useMemo(() => {
		return Object.entries(normalizedLocations).sort(([a], [b]) =>
			a.localeCompare(b),
		);
	}, [normalizedLocations]);

	/**
	 * 1. **Initialize collapsedStates based on selectedLocation**
	 *    Runs only once on mount.
	 */
	useEffect(() => {
		const initialCollapsed = {};
		Object.keys(normalizedLocations).forEach((stateAbbr) => {
			// If the state has any selected locations, don't collapse it
			const hasSelectedLocations = selectedLocation[stateAbbr]
				? Object.values(selectedLocation[stateAbbr]).some(
						(isSelected) => isSelected,
				  )
				: false;
			initialCollapsed[stateAbbr] = !hasSelectedLocations; // false = expanded, true = collapsed
		});
		setCollapsedStates(initialCollapsed);
		// We only want to set this once on mount, hence no dependencies
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Empty dependency array ensures this runs only once on mount

	/**
	 * 2. **Update collapsedStates when selectedLocation changes**
	 */
	useEffect(() => {
		setCollapsedStates((prevCollapsed) => {
			const newCollapsed = { ...prevCollapsed };
			Object.keys(normalizedLocations).forEach((stateAbbr) => {
				const hasSelectedLocations = selectedLocation[stateAbbr]
					? Object.values(selectedLocation[stateAbbr]).some(
							(isSelected) => isSelected,
					  )
					: false;
				// Only update if the current collapse state doesn't match the desired state
				if (hasSelectedLocations && newCollapsed[stateAbbr]) {
					newCollapsed[stateAbbr] = false; // Expand if it has selected locations
				} else if (!hasSelectedLocations && !newCollapsed[stateAbbr]) {
					newCollapsed[stateAbbr] = true; // Collapse if no selected locations
				}
			});
			return newCollapsed;
		});
	}, [selectedLocation, normalizedLocations]);

	/**
	 * 3. **Define fetchMakesByCategory before using it in useEffect**
	 *    Ensures that the function is defined before any useEffect that uses it.
	 */
	const fetchMakesByCategory = useCallback(
		(categorySlug) => {
			setIsLoading(true);
			const category = categories.find((cat) => cat.slug === categorySlug);
			const categoryID = category ? category.term_id : null;

			if (!categoryID) {
				console.warn("Awaiting Category Information for Slug: ", categorySlug);
				setIsLoading(false);
				return;
			}

			fetch(rtsAjax.ajax_url, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: new URLSearchParams({
					action: "get_makes_by_category",
					nonce: rtsAjax.nonce,
					category_id: categoryID,
				}),
			})
				.then((response) => response.json())
				.then((response) => {
					if (response.success && response.data.makes) {
						// console.log("full response make by category", response);
						const makesArray = Object.values(response.data.makes);
						if (
							JSON.stringify(makesArray) !==
							JSON.stringify(availableMakesRef.current)
						) {
							setAvailableMakes();
							// console.log("available makes: ", availableMakes);
							setAvailableMakes(makesArray);
							// console.log("available makes: ", availableMakes);
						}
						if (
							response.data.locations &&
							JSON.stringify(response.data.locations) !==
								JSON.stringify(availableLocationsRef.current)
						) {
							setAvailableLocations(response.data.locations);
						}
						if (
							!yearChangedByUser &&
							response.data.min_year &&
							response.data.max_year
						) {
							setYearValues([
								Math.max(YEARRANGEMIN, parseInt(response.data.min_year, 10)),
								Math.min(YEARRANGEMAX, parseInt(response.data.max_year, 10)),
							]);
						}
						if (
							!priceChangedByUser &&
							response.data.min_price &&
							response.data.max_price
						) {
							setPriceValues([
								Number(response.data.min_price || PRICEMIN),
								Number(response.data.max_price || PRICEMAX),
							]);
						}
						if (
							!mileageChangedByUser &&
							response.data.min_mileage &&
							response.data.max_mileage
						) {
							setMileageValues([
								Number(response.data.min_mileage || MILEAGEMIN),
								Number(response.data.max_mileage || MILEAGEMAX),
							]);
						}

						console.log("response data", response.data);
						setCategoryInfo({
							category_name: response.data.category_name || "",
							category_description: response.data.category_description || "",
						});

						setIsLoading(false);
					} else {
						console.error("No makes found:", response.data);
						setIsLoading(false);
					}
				})
				.catch((error) => {
					console.error("Error fetching makes:", error);
					setCategoryInfo({
						category_name: "",
						category_description: "",
					});
					setIsLoading(false);
				});
		},
		[categories, yearChangedByUser, priceChangedByUser, mileageChangedByUser],
	);

	/**
	 * 4. **Define fetchModelsByMake before using it in useEffect**
	 *    Ensures that the function is defined before any useEffect that uses it.
	 */
	const fetchModelsByMake = useCallback(
		(makeID, categorySlug) => {
			setIsLoading(true);
			const category = categories.find((cat) => cat.slug === categorySlug);
			const categoryID = category ? category.term_id : null;

			if (!makeID) {
				console.error("Invalid make ID:", makeID);
				setIsLoading(false);
				return;
			}

			fetch(rtsAjax.ajax_url, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: new URLSearchParams({
					action: "get_models_by_make",
					nonce: rtsAjax.nonce,
					category_id: categoryID || 0,
					make_id: makeID,
				}),
			})
				.then((response) => response.json()) // Parse the response as JSON
				.then((response) => {
					if (response.success && response.data.models) {
						const modelsArray = Object.values(response.data.models);
						if (
							JSON.stringify(modelsArray) !==
							JSON.stringify(availableModelsRef.current)
						) {
							setAvailableModels(modelsArray);
						}

						// Set selectedModels if initialModelsRef.current is set
						if (initialModelsRef.current.length > 0) {
							const validModels = initialModelsRef.current.flatMap((modelIDs) =>
								modelIDs
									.split(",")
									.filter((modelID) =>
										modelsArray.some(
											(model) => model.modelID.toString() === modelID,
										),
									),
							);
							setSelectedModels(validModels);
							initialModelsRef.current = []; // Reset the ref
						}

						if (
							response.data.locations &&
							JSON.stringify(response.data.locations) !==
								JSON.stringify(availableLocationsRef.current)
						) {
							setAvailableLocations(response.data.locations);
						}
						// Update year range only if user hasn't changed it
						if (
							!yearChangedByUser &&
							response.data.min_year &&
							response.data.max_year
						) {
							setYearValues([
								Math.max(YEARRANGEMIN, parseInt(response.data.min_year, 10)),
								Math.min(YEARRANGEMAX, parseInt(response.data.max_year, 10)),
							]);
						}

						// Update price range only if user hasn't changed it
						if (
							!priceChangedByUser &&
							response.data.min_price &&
							response.data.max_price
						) {
							setPriceValues([
								Number(response.data.min_price || PRICEMIN),
								Number(response.data.max_price || PRICEMAX),
							]);
						}

						// Update mileage range only if user hasn't changed it
						if (
							!mileageChangedByUser &&
							response.data.min_mileage &&
							response.data.max_mileage
						) {
							setMileageValues([
								Number(response.data.min_mileage || MILEAGEMIN),
								Number(response.data.max_mileage || MILEAGEMAX),
							]);
						}

						setIsLoading(false);
					} else {
						// **Important Update: Do not clear makes if category is invalid or fetch fails**
						setAvailableModels([]);
						setAvailableLocations([]);
						console.error("No models found:", response.data);
						setIsLoading(false);
					}
				})
				.catch((error) => {
					console.error("Error fetching models:", error);
					// **Important Update: Do not clear makes on fetch error**
					setAvailableModels([]);
					setAvailableLocations([]);
					setIsLoading(false);
				});
		},
		[categories, yearChangedByUser, priceChangedByUser, mileageChangedByUser],
	);

	/**
	 * 5. **Fetch filtered products when relevant filters change, only after user interaction**
	 */
	const fetchFilteredProducts = useCallback(() => {
		if (isFetching) return; // Prevent making a new request if one is already in progress
		setIsFetching(true);
		setIsLoading(true);

		// **Prepare filter parameters**
		const selectedLocations = getSelectedLocationKeys();
		const filterValues = {
			action: "rts_update_products",
			nonce: rtsAjax.nonce,
			category: selectedCategory,
			make: selectedMake,
			models: selectedModels,
			min_price: priceValues[0],
			max_price: priceValues[1],
			min_year: yearValues[0],
			max_year: yearValues[1],
			min_mileage: mileageValues[0],
			max_mileage: mileageValues[1],
			sort: sortField,
			paged: 1, // Reset to first page on filter change
		};

		// **Add location only if any are selected**
		if (selectedLocations.length > 0) {
			filterValues.location = selectedLocations;
		}

		// Fetch the category info based on the selectedCategory directly
		const selectedCategoryData = categories.find(
			(category) => category.slug === selectedCategory,
		);

		// Check if selectedCategoryData is found, else set default info
		const currentCategoryInfo = selectedCategoryData
			? {
					category_name: selectedCategoryData.name || "",
					category_description: selectedCategoryData.description || "",
			  }
			: {
					category_name: "All Categories",
					category_description: "No category selected",
			  };

		console.log("currentCategoryInfo", currentCategoryInfo);

		// **Use qs to properly encode the data, including arrays**
		const bodyData = qs.stringify(filterValues, { arrayFormat: "indices" });

		// **AJAX call to update products**
		fetch(rtsAjax.ajax_url, {
			method: "POST",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
			},
			body: bodyData,
		})
			.then((response) => response.json())
			.then((response) => {
				if (response.success) {
					const products = response.data.products;
					const pagination = response.data.pagination;

					window.updateProductDetails(
						products,
						pagination,
						currentCategoryInfo, // Pass the updated category info
					);
				} else {
					console.error("No products found:", response);
					window.updateProductDetails([], "", currentCategoryInfo);
				}
				setIsLoading(false);
				setIsFetching(false);
				setFiltersChanged(false); // **Reset the flag here**
			})
			.catch((error) => {
				console.error("Error fetching updated products:", error);
				window.updateProductDetails([], "", currentCategoryInfo);
				setIsLoading(false);
				setIsFetching(false);
				setFiltersChanged(false); // **Reset the flag even on error**
			});
	}, [
		selectedCategory, // Ensure this is the latest category selected
		selectedMake,
		selectedModels,
		priceValues,
		yearValues,
		mileageValues,
		selectedLocation,
		sortField,
		isFetching,
		categories, // Ensure categories are up-to-date
	]);

	// **6. Define debouncedFetchFilteredProducts before using it in useEffect**
	const debouncedFetchFilteredProducts = useMemo(
		() => debounce(fetchFilteredProducts, 300),
		[fetchFilteredProducts],
	);

	/**
	 * 7. **Fetch filtered products when relevant filters change, only after user interaction**
	 */
	useEffect(() => {
		if (!filtersChanged) {
			// **Skip fetching if filters haven't changed due to user interaction**
			return;
		}
		// **Fetch filtered products on filter change**
		debouncedFetchFilteredProducts();

		// **Cleanup debounce on unmount**
		return () => {
			debouncedFetchFilteredProducts.cancel();
		};
	}, [
		selectedCategory,
		selectedMake,
		selectedModels,
		priceValues,
		yearValues,
		mileageValues,
		selectedLocation,
		filtersChanged,
		debouncedFetchFilteredProducts,
	]);

	/**
	 * 8. **Event Listener for Sorting Changes**
	 */
	useEffect(() => {
		const handleSortingChanged = (event) => {
			// console.log("handleSortingChanged");
			const newSortOption = event.detail.sortOption;
			setSortField(newSortOption);
			setFiltersChanged(true); // **Trigger fetchFilteredProducts**
		};

		// **Use a flag to ensure listener is added only once**
		if (!window.__sortingChangedListenerAdded) {
			window.addEventListener("sortingChanged", handleSortingChanged);
			window.__sortingChangedListenerAdded = true;
		}

		// **Cleanup function to remove the listener on unmount**
		return () => {
			if (window.__sortingChangedListenerAdded) {
				window.removeEventListener("sortingChanged", handleSortingChanged);
				window.__sortingChangedListenerAdded = false;
			}
		};
	}, []);

	/**
	 * 9. **Handle slider value changes**
	 */
	const handleSliderChange = (values, setValueFunc, setChangedFlagFunc) => {
		setValueFunc(values);
		setChangedFlagFunc(true); // Mark as changed by user
		setFiltersChanged(true);
	};

	/**
	 * 10. **Utility functions**
	 */
	const unformatNumber = (value) => {
		if (typeof value === "number") {
			value = value.toString();
		}
		return value.replace(/,/g, "");
	};

	const handleModelChange = (modelId) => {
		setSelectedModels((prevSelectedModels) => {
			if (prevSelectedModels.includes(modelId)) {
				return prevSelectedModels.filter((id) => id !== modelId);
			} else {
				return [...prevSelectedModels, modelId];
			}
		});
		setFiltersChanged(true);
	};

	const toggleLocationsCollapse = () => {
		setIsLocationsCollapsed((prevState) => !prevState);
	};

	/**
	 * 11. **Handle location checkbox change**
	 */
	const handleLocationChange = (event, stateAbbr, locationSlug) => {
		const { checked } = event.target;
		const newSelected = {
			...selectedLocation,
			[stateAbbr]: {
				...(selectedLocation[stateAbbr] || {}),
				[locationSlug]: checked,
			},
		};

		// Remove state key if no locations are selected
		if (newSelected[stateAbbr]) {
			const hasSelected = Object.values(newSelected[stateAbbr]).some(
				(val) => val,
			);
			if (!hasSelected) {
				delete newSelected[stateAbbr];
			}
		}

		setSelectedLocation(newSelected);
		setFiltersChanged(true);
	};

	/**
	 * 12. **Update partialSelectionStates when selectedLocation changes**
	 */
	useEffect(() => {
		const newPartialSelectionStates = {};
		Object.keys(normalizedLocations).forEach((stateAbbr) => {
			const locations = normalizedLocations[stateAbbr] || [];
			const totalLocations = locations.length;
			const selectedLocations = selectedLocation[stateAbbr]
				? Object.values(selectedLocation[stateAbbr]).filter(
						(isSelected) => isSelected,
				  )
				: [];

			if (
				selectedLocations.length > 0 &&
				selectedLocations.length < totalLocations
			) {
				newPartialSelectionStates[stateAbbr] = true;
			} else {
				newPartialSelectionStates[stateAbbr] = false;
			}
		});
		setPartialSelectionStates(newPartialSelectionStates);
	}, [selectedLocation, normalizedLocations]);

	/**
	 * 13. **Handle state checkbox change (select/deselect all locations)**
	 */
	const handleStateChange = (event, stateAbbr) => {
		const { checked } = event.target;

		const newSelected = { ...selectedLocation };

		normalizedLocations[stateAbbr].forEach((location) => {
			if (!newSelected[stateAbbr]) {
				newSelected[stateAbbr] = {};
			}
			newSelected[stateAbbr][location.slug] = checked;
		});

		setSelectedLocation(newSelected);
		setFiltersChanged(true);
	};

	/**
	 * 14. **Modify the toggleCollapseState to expand/collapse only when clicking outside checkbox or label**
	 */
	const toggleCollapseStateHandler = (stateAbbr, event) => {
		// **Prevent expanding/collapsing if the click target is a checkbox or label**
		if (event.target.tagName === "INPUT" || event.target.tagName === "LABEL") {
			return;
		}
		setCollapsedStates((prevCollapsed) => ({
			...prevCollapsed,
			[stateAbbr]: !prevCollapsed[stateAbbr],
		}));
	};

	/**
	 * 15. **Flattening Location Array**
	 */
	const getSelectedLocationKeys = () => {
		const keys = [];
		Object.values(selectedLocation).forEach((stateLocations) => {
			Object.entries(stateLocations).forEach(([locationSlug, isSelected]) => {
				if (isSelected) {
					keys.push(locationSlug);
				}
			});
		});

		return keys;
	};

	/**
	 * 16. **Render functions for sliders**
	 */
	const renderTrackPrice = useCallback(
		({ props, children }) => {
			return (
				<div
					{...props}
					style={{
						...props.style,
						background: getTrackBackground({
							values: priceValues,
							colors: [
								"var(--wp--preset--color--tertiary)",
								"var(--wp--preset--color--quaternary)",
								"var(--wp--preset--color--tertiary)",
							],
							min: PRICEMIN,
							max: PRICEMAX,
						}),
						borderRadius: "0",
						height: "2px",
						margin: "auto",
						width: "calc(100% - 10px)",
					}}
				>
					{children}
				</div>
			);
		},
		[priceValues],
	);

	const renderThumbPrice = useCallback(({ props, isDragged }) => {
		const { key, ...restProps } = props;
		return (
			<div
				key={key}
				{...restProps}
				style={{
					...restProps.style,
					alignItems: "center",
					backgroundColor: isDragged
						? "var(--wp--preset--color--quaternary)"
						: "var(--wp--preset--color--tertiary)",
					borderRadius: "0",
					border: "2px solid var(--wp--preset--color--contrast)",
					display: "flex",
					height: "10px",
					justifyContent: "center",
					outline: "none",
					margin: "0 -10px",
					width: "10px",
				}}
			/>
		);
	}, []);

	const renderTrackYear = useCallback(
		({ props, children }) => {
			return (
				<div
					{...props}
					style={{
						...props.style,
						background: getTrackBackground({
							values: yearValues,
							colors: [
								"var(--wp--preset--color--tertiary)",
								"var(--wp--preset--color--quaternary)",
								"var(--wp--preset--color--tertiary)",
							],
							min: YEARRANGEMIN,
							max: YEARRANGEMAX,
						}),
						borderRadius: "0",
						height: "2px",
						margin: "auto",
						width: "calc(100% - 10px)",
					}}
				>
					{children}
				</div>
			);
		},
		[yearValues],
	);

	const renderThumbYear = useCallback(({ props, isDragged }) => {
		const { key, ...restProps } = props;
		return (
			<div
				key={key}
				{...restProps}
				style={{
					...restProps.style,
					alignItems: "center",
					backgroundColor: isDragged
						? "var(--wp--preset--color--quaternary)"
						: "var(--wp--preset--color--tertiary)",
					borderRadius: "0",
					border: "2px solid var(--wp--preset--color--contrast)",
					display: "flex",
					height: "10px",
					justifyContent: "center",
					outline: "none",
					margin: "0 -10px",
					width: "10px",
				}}
			/>
		);
	}, []);

	const renderTrackMileage = useCallback(
		({ props, children }) => {
			return (
				<div
					{...props}
					style={{
						...props.style,
						background: getTrackBackground({
							values: mileageValues,
							colors: [
								"var(--wp--preset--color--tertiary)",
								"var(--wp--preset--color--quaternary)",
								"var(--wp--preset--color--tertiary)",
							],
							min: MILEAGEMIN,
							max: MILEAGEMAX,
						}),
						borderRadius: "0",
						height: "2px",
						margin: "auto",
						width: "calc(100% - 10px)",
					}}
				>
					{children}
				</div>
			);
		},
		[mileageValues],
	);

	const renderThumbMileage = useCallback(({ props, isDragged }) => {
		const { key, ...restProps } = props;
		return (
			<div
				key={key}
				{...restProps}
				style={{
					...restProps.style,
					alignItems: "center",
					backgroundColor: isDragged
						? "var(--wp--preset--color--quaternary)"
						: "var(--wp--preset--color--tertiary)",
					borderRadius: "0",
					border: "2px solid var(--wp--preset--color--contrast)",
					display: "flex",
					height: "10px",
					justifyContent: "center",
					outline: "none",
					margin: "0 -10px",
					width: "10px",
				}}
			/>
		);
	}, []);

	/**
	 * 17. **Handle Search Submit (if you have a search button)**
	 */
	const handleSearchSubmit = (e) => {
		e.preventDefault();

		if (formRef.current) {
			const priceMinInput = formRef.current.querySelector(
				'input[name="detailed_search[price_min]"]',
			);
			const priceMaxInput = formRef.current.querySelector(
				'input[name="detailed_search[price_max]"]',
			);

			if (priceMinInput)
				priceMinInput.value = unformatNumber(priceMinInput.value);
			if (priceMaxInput)
				priceMaxInput.value = unformatNumber(priceMaxInput.value);

			formRef.current.submit();
		}
	};

	/**
	 * 18. **Event handlers for selects**
	 */
	const onCategoryChange = (e) => {
		const newCategory = e.target.value;
		console.log("newCategory: ", newCategory);

		// Reset make and models when a new category is selected
		setSelectedMake("");
		setSelectedModels([]);

		// Reset other filters
		setSelectedLocation({});
		setPriceValues([PRICEMIN, PRICEMAX]);
		setYearValues([YEARRANGEMIN, YEARRANGEMAX]);
		setMileageValues([MILEAGEMIN, MILEAGEMAX]);
		setSortField("");

		// Reset interaction flags
		setPriceChangedByUser(false);
		setYearChangedByUser(false);
		setMileageChangedByUser(false);

		// Check if the selected category exists in categories
		const categoryExists = categories.some(
			(category) => category.slug === newCategory,
		);

		if (categoryExists) {
			setSelectedCategory(newCategory);
		} else {
			// Handle the case where the category doesn't exist
			setSelectedCategory(""); // Or set a default category slug, or show a message
		}

		setFiltersChanged(true);
	};

	const onMakeChange = (e) => {
		setSelectedMake(e.target.value);
		if (initialParamsLoaded) {
			setSelectedModels([]);
		}
		setFiltersChanged(true);
	};

	/**
	 * 19. **Handle Clear Filters**
	 */
	const handleClearFilters = () => {
		// **Reset all state variables to their initial values**
		setSelectedCategory("");
		setSelectedMake("");
		setSelectedModels([]);
		setSelectedLocation({});
		setPriceValues([PRICEMIN, PRICEMAX]);
		setYearValues([YEARRANGEMIN, YEARRANGEMAX]);
		setMileageValues([MILEAGEMIN, MILEAGEMAX]);
		setSortField("");
		setAvailableMakes(makes);
		setAvailableModels(models);
		setAvailableLocations(locations);
		setIsLocationsCollapsed(false);
		setCollapsedStates({}); // Reset collapsedStates to trigger initialization

		// Reset interaction flags
		setPriceChangedByUser(false);
		setYearChangedByUser(false);
		setMileageChangedByUser(false);

		// **Reset URL parameters**
		resetURLParameters();

		// **Indicate that filters have changed to trigger product fetch**
		setFiltersChanged(true);
	};

	/**
	 * 20. **Reset URL Parameters**
	 */
	const resetURLParameters = () => {
		const params = new URLSearchParams(window.location.search);

		// **Remove all detailed_search parameters**
		for (const key of Array.from(params.keys())) {
			if (key.startsWith("detailed_search[")) {
				params.delete(key);
			}
		}

		// **Update the browser's URL without reloading the page**
		const newUrl = params.toString()
			? `${window.location.pathname}?${params.toString()}`
			: window.location.pathname;
		window.history.replaceState({}, "", newUrl);
	};

	/**
	 * 21. **Parse URL parameters on mount**
	 */
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		// console.log("URL Params:", params);
		const detailedSearchParams = {};

		for (const [key, value] of params.entries()) {
			const match = key.match(/^detailed_search\[(.+)\]$/);
			if (match) {
				const paramName = match[1];
				if (paramName.endsWith("[]")) {
					const arrayParamName = paramName.slice(0, -2);
					if (!detailedSearchParams[arrayParamName]) {
						detailedSearchParams[arrayParamName] = [];
					}
					detailedSearchParams[arrayParamName].push(value);
				} else {
					detailedSearchParams[paramName] = value;
				}
			}
		}

		const categoryParam = detailedSearchParams["category"];
		const makeParam = detailedSearchParams["make"];
		const modelsParam = detailedSearchParams["model"]
			? [detailedSearchParams["model"]].flat()
			: [];
		const locationParam = detailedSearchParams["location"];
		const priceMinParam = detailedSearchParams["min_price"];
		const priceMaxParam = detailedSearchParams["max_price"];
		const yearMinParam = detailedSearchParams["min_year"];
		const yearMaxParam = detailedSearchParams["max_year"];
		const mileageMinParam = detailedSearchParams["min_mileage"];
		const mileageMaxParam = detailedSearchParams["max_mileage"];
		const sortParam = detailedSearchParams["sort"] || "";

		// console.log("Detailed Search Params:", detailedSearchParams);

		// **Set Category**
		if (categoryParam) {
			setSelectedCategory(categoryParam);
		}

		// **Set Make**
		if (makeParam) {
			initialMakeIDRef.current = makeParam.toString(); // Ensure it's a string
		}

		// **Set Models**
		if (modelsParam.length > 0) {
			initialModelsRef.current = modelsParam; // Store in ref
		}

		// **Parse and Set Locations**
		if (locationParam) {
			// console.log("locationParam:", locationParam);

			const locationObj = {};
			let locationsArray = [];

			// **Normalize the locationParam to an array of individual location strings**
			if (Array.isArray(locationParam)) {
				// If it's an array, split each element by ',' and concatenate the results
				locationParam.forEach((locale) => {
					// Ensure locale is a string before splitting
					if (typeof locale === "string") {
						locationsArray = locationsArray.concat(locale.split(","));
					}
				});
			} else if (typeof locationParam === "string") {
				// If it's a single string, split by ',' to get individual locations
				locationsArray = locationParam.split(",");
			}

			/**
			 * **Splits a string by the last hyphen.**
			 * Example: 'kansas-city-mo' => ['kansas-city', 'MO']
			 * @param {string} str - The string to split.
			 * @returns {Array} - An array containing [location, stateAbbr].
			 */
			function splitByLastHyphen(str) {
				const lastHyphen = str.lastIndexOf("-");
				if (lastHyphen === -1) {
					return [str.trim(), ""];
				}
				const location = str.substring(0, lastHyphen).trim();
				const stateAbbr = str
					.substring(lastHyphen + 1)
					.trim()
					.toUpperCase(); // Convert to uppercase
				return [location, stateAbbr];
			}

			// **Iterate over each individual location string to build locationObj**
			locationsArray.forEach((locKey) => {
				const [location, stateAbbr] = splitByLastHyphen(locKey);

				// **Validate the split results**
				if (!location || !stateAbbr) {
					console.warn(
						`Invalid location format: "${locKey}". Expected "location-stateAbbr".`,
					);
					return;
				}

				// **Initialize the stateAbbr object if it doesn't exist**
				if (!locationObj[stateAbbr]) {
					locationObj[stateAbbr] = {};
				}

				// **Assign the location within the stateAbbr using locKey as the key**
				locationObj[stateAbbr][locKey] = true;
			});

			// console.log("locationObj:", locationObj);
			setSelectedLocation(locationObj);
		}

		// Set Price Range
		if (priceMinParam && priceMaxParam) {
			const parsedPriceMin = parseInt(priceMinParam, 10);
			const parsedPriceMax = parseInt(priceMaxParam, 10);
			if (!isNaN(parsedPriceMin) && !isNaN(parsedPriceMax)) {
				setPriceValues([
					Math.max(PRICEMIN, parsedPriceMin),
					Math.min(PRICEMAX, parsedPriceMax),
				]);
				setPriceChangedByUser(true);
			}
		}

		// Set Year Range
		if (yearMinParam && yearMaxParam) {
			const parsedYearMin = parseInt(yearMinParam, 10);
			const parsedYearMax = parseInt(yearMaxParam, 10);
			if (!isNaN(parsedYearMin) && !isNaN(parsedYearMax)) {
				setYearValues([
					Math.max(YEARRANGEMIN, parsedYearMin),
					Math.min(YEARRANGEMAX, parsedYearMax),
				]);
				setYearChangedByUser(true);
			}
		}

		// Set Mileage Range
		if (mileageMinParam && mileageMaxParam) {
			const parsedMileageMin = parseInt(mileageMinParam, 10);
			const parsedMileageMax = parseInt(mileageMaxParam, 10);
			if (!isNaN(parsedMileageMin) && !isNaN(parsedMileageMax)) {
				setMileageValues([
					Math.max(MILEAGEMIN, parsedMileageMin),
					Math.min(MILEAGEMAX, parsedMileageMax),
				]);
				setMileageChangedByUser(true);
			}
		}

		// **Set Sort Field**
		setSortField(sortParam);
		setInitialParamsLoaded(true);
		setIsLoading(false);
	}, []);
	// [categories, availableLocations, makes, models]); // **Dependencies based on props**

	/**
	 * 22. **Fetch makes when selectedCategory changes**
	 */
	useEffect(() => {
		if (isFetching) return;

		if (!initialParamsLoaded) {
			// console.log("useEffect [selectedMake] - Initial params not loaded");
			return;
		}

		if (categories.length === 0) {
			// console.log("useEffect [selectedMake] - Categories not loaded yet");
			return;
		}

		if (selectedMake) {
			// console.log("useEffect [selectedMake] - Selected make:", selectedMake); // Log selectedMake
			fetchModelsByMake(selectedMake, selectedCategory);
		} else {
			// console.log(
			// 	"useEffect [selectedMake] - No make selected, resetting availableModels",
			// );
			setAvailableModels(models);
		}
	}, [
		selectedMake,
		selectedCategory,
		categories,
		models,
		fetchModelsByMake,
		isFetching,
	]);

	/**
	 * 23. **Fetch models when selectedMake changes**
	 */
	useEffect(() => {
		if (isFetching) return;

		if (!initialParamsLoaded) {
			// Wait until initial parameters are loaded
			return;
		}

		if (categories.length === 0) {
			// **Categories not loaded yet**
			return;
		}

		if (selectedMake) {
			// console.log('selected make set firing fetch models');
			fetchModelsByMake(selectedMake, selectedCategory);
		} else {
			setAvailableModels(models);
		}
	}, [
		selectedMake,
		selectedCategory,
		categories,
		models,
		fetchModelsByMake,
		isFetching,
	]);

	/**
	 * 25. **Set selectedMake after availableMakes are updated**
	 */
	useEffect(() => {
		if (initialMakeIDRef.current) {
			const makeExists = availableMakes.some(
				(make) => make.makeID.toString() === initialMakeIDRef.current,
			);
			if (makeExists) {
				setSelectedMake(initialMakeIDRef.current);
			} else {
				console.warn(
					`Make with ID ${initialMakeIDRef.current} not found in available makes.`,
				);
				setSelectedMake(""); // Reset if not found
			}
		}
	}, [availableMakes, initialMakeIDRef]);

	// **Return Statement (Render)**
	return (
		<div className={`sidebar-filters ${isMobile ? "mobile" : "desktop"}`}>
			{isMobile && (
				<div className="hide-filters-mobile" onClick={toggleFilters}>
					<FilterIcon /> {filtersHidden ? "Show Filters" : "Hide Filters"}
				</div>
			)}
			{/* **Category Selection** */}
			<div className="sidebar-filter category-selection">
				<h2>Category</h2>
				<div className="filter-option">
					<select value={selectedCategory} onChange={onCategoryChange}>
						<option value="">All Categories</option>

						{/* Render Parent Categories with Children as Optgroups */}
						{topLevelCategories.map((parentCategory) =>
							groupedCategories[parentCategory.term_id] &&
							groupedCategories[parentCategory.term_id].length > 0 ? (
								<optgroup
									key={parentCategory.term_id}
									label={parentCategory.name}
								>
									{/* Option to select the parent category itself */}
									<option
										value={parentCategory.slug}
										data-catid={parentCategory.term_id}
									>
										{parentCategory.name}
									</option>
									{/* Render Child Categories */}
									{groupedCategories[parentCategory.term_id].map(
										(childCategory) => (
											<option
												key={childCategory.term_id}
												value={childCategory.slug}
												data-catid={childCategory.term_id}
												className="child-category"
											>
												{childCategory.name}
											</option>
										),
									)}
								</optgroup>
							) : (
								// Render as standalone option if no children
								<option
									key={parentCategory.term_id}
									value={parentCategory.slug}
									data-catid={parentCategory.term_id}
								>
									{parentCategory.name}
								</option>
							),
						)}
					</select>
				</div>
			</div>

			{/* **Make and Model Selection** */}
			<div className="sidebar-filter make-model-selection">
				<h2>Make & Model</h2>
				<div className="filter-option">
					<label>Make</label>
					<select value={selectedMake} onChange={onMakeChange}>
						<option value="">Select Make</option>
						{makesToDisplay.map((make) => (
							<option key={make.makeID} value={make.makeID.toString()}>
								{make.makeName}
							</option>
						))}
					</select>
				</div>

				{modelsToDisplay.length > 0 && (
					<div className="filter-option">
						<label>Models</label>
						{modelsToDisplay.map((model) => (
							<div key={model.modelID} className="filter-checkbox">
								<input
									type="checkbox"
									id={`model-${model.modelID}`}
									value={model.modelID}
									checked={selectedModels.includes(model.modelID)}
									onChange={() => handleModelChange(model.modelID)}
								/>
								<label htmlFor={`model-${model.modelID}`}>
									{model.modelName}
								</label>
							</div>
						))}
					</div>
				)}
			</div>

			{/* **Price Range Slider** */}
			<div className="sidebar-filter price-range-slider">
				<h2>Price</h2>
				<div className="filter-option">
					<Range
						values={priceValues}
						step={1000}
						min={PRICEMIN}
						max={PRICEMAX}
						onChange={(values) =>
							handleSliderChange(values, setPriceValues, setPriceChangedByUser)
						}
						renderTrack={renderTrackPrice}
						renderThumb={renderThumbPrice}
					/>
					<div className="price-range-values range-values">
						<input
							type="number"
							value={priceValues[0]}
							min={PRICEMIN}
							max={priceValues[1]}
							onChange={(e) => {
								const value = Math.max(
									PRICEMIN,
									Math.min(+e.target.value, priceValues[1]),
								);
								handleSliderChange(
									[value, priceValues[1]],
									setPriceValues,
									setPriceChangedByUser,
								);
							}}
						/>
						<span></span>
						<input
							type="number"
							value={priceValues[1]}
							min={priceValues[0]}
							max={PRICEMAX}
							onChange={(e) => {
								const value = Math.min(
									PRICEMAX,
									Math.max(+e.target.value, priceValues[0]),
								);
								handleSliderChange(
									[priceValues[0], value],
									setPriceValues,
									setPriceChangedByUser,
								);
							}}
						/>
					</div>
				</div>
			</div>

			{/* **Locations** */}
			<div className="sidebar-filter location-selection">
				<h2
					className="wp-block-heading has-base-color has-text-color has-link-color"
					onClick={toggleLocationsCollapse}
					style={{ cursor: "pointer" }}
				>
					Locations
					<span
						className={`triangle ${
							isLocationsCollapsed ? "collapsed" : "expanded"
						}`}
					></span>
				</h2>

				{!isLocationsCollapsed && (
					<div className="filter-option">
						{sortedLocations.map(([stateAbbr, locations]) => {
							const isCollapsed = collapsedStates[stateAbbr];
							const isAllSelected = locations.every(
								(location) => selectedLocation[stateAbbr]?.[location.slug],
							);
							const stateName = convertUsStateAbbrAndName(stateAbbr);

							return (
								<div key={stateAbbr} className="location-group">
									<div
										className="state-header filter-checkbox location"
										onClick={(e) => toggleCollapseStateHandler(stateAbbr, e)}
									>
										<input
											id={`state-${stateAbbr}`}
											type="checkbox"
											checked={isAllSelected}
											className={
												partialSelectionStates[stateAbbr]
													? "partial-selection"
													: ""
											}
											onChange={(e) => handleStateChange(e, stateAbbr)}
										/>
										<label htmlFor={`state-${stateAbbr}`}>
											{convertUsStateAbbrAndName(stateAbbr)}
										</label>
										<span
											className={`triangle ${
												isCollapsed ? "collapsed" : "expanded"
											}`}
										></span>
									</div>

									{!isCollapsed && (
										<div className="state-locations">
											{locations.map((location) => (
												<div
													className="filter-checkbox location"
													key={location.slug}
												>
													<input
														type="checkbox"
														id={`location-${location.slug}`}
														value={location.slug}
														checked={
															selectedLocation[stateAbbr]?.[location.slug] ||
															false
														}
														onChange={(e) =>
															handleLocationChange(e, stateAbbr, location.slug)
														}
													/>
													<label htmlFor={`location-${location.slug}`}>
														{location.value}
													</label>
												</div>
											))}
										</div>
									)}
								</div>
							);
						})}
					</div>
				)}
			</div>

			{/* **Year Range Slider** */}
			<div className="sidebar-filter year-range-slider">
				<h2>Year</h2>
				<div className="filter-option">
					<Range
						values={yearValues}
						step={1}
						min={YEARRANGEMIN}
						max={YEARRANGEMAX}
						onChange={(values) =>
							handleSliderChange(values, setYearValues, setYearChangedByUser)
						}
						renderTrack={renderTrackYear}
						renderThumb={renderThumbYear}
					/>
					<div className="year-range-values range-values">
						<input
							type="number"
							value={yearValues[0]}
							min={YEARRANGEMIN}
							max={yearValues[1]}
							onChange={(e) => {
								const value = Math.max(
									YEARRANGEMIN,
									Math.min(+e.target.value, yearValues[1]),
								);
								handleSliderChange(
									[value, yearValues[1]],
									setYearValues,
									setYearChangedByUser,
								);
							}}
						/>
						<span></span>
						<input
							type="number"
							value={yearValues[1]}
							min={yearValues[0]}
							max={YEARRANGEMAX}
							onChange={(e) => {
								const value = Math.min(
									YEARRANGEMAX,
									Math.max(+e.target.value, yearValues[0]),
								);
								handleSliderChange(
									[yearValues[0], value],
									setYearValues,
									setYearChangedByUser,
								);
							}}
						/>
					</div>
				</div>
			</div>

			{/* **Mileage Range Slider** */}
			<div className="sidebar-filter mileage-range-slider">
				<h2>Mileage</h2>
				<div className="filter-option">
					<Range
						values={mileageValues}
						step={1000}
						min={MILEAGEMIN}
						max={MILEAGEMAX}
						onChange={(values) =>
							handleSliderChange(
								values,
								setMileageValues,
								setMileageChangedByUser,
							)
						}
						renderTrack={renderTrackMileage}
						renderThumb={renderThumbMileage}
					/>
					<div className="mileage-range-values range-values">
						<input
							type="number"
							value={mileageValues[0]}
							min={MILEAGEMIN}
							max={mileageValues[1]}
							onChange={(e) => {
								const value = Math.max(
									MILEAGEMIN,
									Math.min(+e.target.value, mileageValues[1]),
								);
								handleSliderChange(
									[value, mileageValues[1]],
									setMileageValues,
									setMileageChangedByUser,
								);
							}}
						/>
						<span></span>
						<input
							type="number"
							value={mileageValues[1]}
							min={mileageValues[0]}
							max={MILEAGEMAX}
							onChange={(e) => {
								const value = Math.min(
									MILEAGEMAX,
									Math.max(+e.target.value, mileageValues[0]),
								);
								handleSliderChange(
									[mileageValues[0], value],
									setMileageValues,
									setMileageChangedByUser,
								);
							}}
						/>
					</div>
				</div>
			</div>

			{/* **Hidden Sort Field** */}
			<input type="hidden" name="detailed_search[sort]" value={sortField} />

			{/* **Clear Filters Button** */}
			<div className="sidebar-filter clear-filters">
				<button onClick={handleClearFilters} className="clear-filters-button">
					Clear All Filters
				</button>
			</div>
			{isLoading && (
				<div className="ajax-loading">
					<img src={loadingGIF} alt="Loading Results" />
				</div>
			)}
		</div>
	);
};

/**
 * **Initialize the React component on DOMContentLoaded**
 */
document.addEventListener("DOMContentLoaded", () => {
	const containers = document.querySelectorAll(
		".wp-block-rts-woo-blocks-sidebar-filters",
	);
	containers.forEach((container) => {
		if (!container._reactRootContainer) {
			const root = createRoot(container);
			container._reactRootContainer = root;

			let attributes = {};
			let categoriesData = [];
			let locationsData = [];
			let makesData = [];
			let modelsData = [];

			try {
				const dataBlock = container.getAttribute("data-block");
				const dataCategories = container.getAttribute("data-categories");
				const dataLocations = container.getAttribute("data-locations");
				const dataMakes = container.getAttribute("data-makes");
				const dataModels = container.getAttribute("data-models");

				if (dataBlock) attributes = JSON.parse(dataBlock);
				if (dataCategories)
					categoriesData = Object.values(JSON.parse(dataCategories));
				if (dataLocations)
					locationsData = Object.values(JSON.parse(dataLocations));
				if (dataMakes) makesData = Object.values(JSON.parse(dataMakes));
				if (dataModels) modelsData = Object.values(JSON.parse(dataModels));
			} catch (error) {
				console.error("Error parsing data:", error);
			}

			root.render(
				<SidebarFiltersComponent
					categories={categoriesData}
					makes={makesData}
					models={modelsData}
					locations={locationsData}
				/>,
			);
		}
	});
});

/**
 * **Update Product Details Function**
 */
export function updateProductDetails(products, paginationLinks, categoryInfo) {
	const container = document.querySelector(
		".wp-block-rts-woo-blocks-product-detail",
	);

	if (container && container._reactRootContainer) {
		// Ensure that ProductDetailComponent is defined and imported
		container._reactRootContainer.render(
			<ProductDetailComponent
				products={products}
				paginationLinks={paginationLinks}
				categoryInfo={categoryInfo}
			/>,
		);
	}
}

export default SidebarFiltersComponent;
